import React, { useState } from "react"
import cx from "classnames"
import { useRender } from "@base-ui/react/use-render"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import { useNavigationContext } from "./navigation-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationPrimaryActionProps = {
  /** Icon shown in both expanded and collapsed states. */
  icon: IconName
  /** Button label — visible when expanded, animated out when collapsed. */
  label: string
  /** Swap the rendered element — e.g. pass `<a href="..." />` or an Inertia `<Link href="..." />`. */
  render?: React.ReactElement
}

// =============================================================================
// Styles
// =============================================================================

const primaryActionStyles = [
  "inline-flex items-center justify-center align-middle text-center",
  "w-full h-10 py-2 px-4 rounded mb-2",
  "font-sans font-semibold text-sm leading-6",
  "border border-solid border-transparent",
  "bg-black text-white hover:bg-gray-800 hover:text-white",
  "no-underline hover:no-underline focus:no-underline focus:outline-none",
  "transition duration-200 cursor-pointer",
].join(" ")

// -ml-3 cancels the icon's mr-2 (8px) and compensates for its -ml-1 (4px) so
// justify-center sees only the icon when collapsed.
const labelStyles = "overflow-hidden whitespace-nowrap transition-[max-width,margin-left] duration-200"

// =============================================================================
// Component
// =============================================================================

/**
 * A full-width primary call-to-action button for the sidebar navigation.
 * Renders the label alongside the icon when expanded, and animates the label
 * out (keeping the icon centered) when the sidebar collapses.
 *
 * Pass a `render` prop to inject a link component for routing.
 *
 * @example
 * <Navigation.PrimaryAction icon="pencil-write" label="Start writing" render={<a href="/campaigns/new/steps" />} />
 */
export function NavigationPrimaryAction({ icon, label, render }: NavigationPrimaryActionProps) {
  const { collapsed } = useNavigationContext()
  const [clicked, setClicked] = useState(false)

  return useRender({
    defaultTagName: "button" as const,
    render,
    props: {
      "data-slot": "navigation-primary-action",
      type: "button" as const,
      className: cx(primaryActionStyles, { "focus:outline-blue": !clicked }),
      onMouseDown: () => setClicked(true),
      onBlur: () => setClicked(false),
      children: (
        <>
          <Icon name={icon} className="-ml-1 mr-2 shrink-0 pointer-events-none" aria-hidden />
          <span className={cx(labelStyles, collapsed ? "max-w-0 -ml-3" : "max-w-xs")}>
            {label}
          </span>
        </>
      )
    }
  })
}

NavigationPrimaryAction.displayName = "Navigation.PrimaryAction"
